import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Input } from '@components/forms';
import { ReactComponent as UploadIcon } from '@assets/icons/upload.svg';
import {
  GET_LENDER_ORGANISATION,
  post,
  UPDATE_LENDER_ORGANISATION
} from '@services';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ReactComponent as ErrorIcon } from '@assets/icons/info_error_light.svg';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RoleEnum } from '@types';
import { useAuthStore } from '@stores';
import UnderlineTabBar from '@components/UnderlineTabBar';
import SettingsSaveBar, { SettingsSaveState } from './SettingsSaveBar';
import SettingsTabNav from './SettingsTabNav';
import {
  SETTINGS_TABS,
  SettingsTabEnum,
  getSettingsTabForField,
  isSettingsTab
} from './settingsTabs';
import MfaSettingPanel from './components/MfaSettingPanel';
import { isMfaSettingEnvironment } from './mfaSettingUtils';

const schema = yup
  .object({
    primaryColour: yup
      .string()
      .matches(/^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/, 'Invalid HEX color')
      .required('Primary color is required'),
    notificationEmail: yup
      .string()
      .required('Please enter notification email.')
      .email('Please provide a valid email address'),
    supportEnquiryEmail: yup
      .string()
      .email('Please provide a valid email address')
      .optional(),
    brokerEmails: yup
      .string()
      .email('Please provide a valid email address')
      .optional(),
    hasActivityHistory: yup.boolean().default(false),
    logo: yup
      .mixed()
      .required('Logo is required')
      .test('fileType', 'Please select a SVG file', (value: any) => {
        return (
          (value && value.file?.type === 'image/svg+xml') ||
          value.url ||
          value.base64
        );
      })
  })
  .required();

const toColorPickerValue = (value?: string) => {
  const match = value?.trim().match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (!match) {
    return '#000000';
  }
  const hex = match[1];
  if (hex.length === 3) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }
  return `#${hex}`;
};

const AccountSetting = () => {
  const {
    register,
    setValue,
    getValues,
    reset,
    watch,
    handleSubmit,
    trigger,
    formState: { errors, isValid, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      primaryColour: '',
      notificationEmail: '',
      supportEnquiryEmail: '',
      brokerEmails: '',
      hasActivityHistory: false,
      logo: {}
    }
  });
  const [previewImage, setPreviewImage] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<SettingsTabEnum>(() =>
    isSettingsTab(tabParam) ? tabParam : SettingsTabEnum.ORGANISATION
  );
  const onDrop = React.useCallback(
    (acceptedFiles: any) => {
      if (acceptedFiles && acceptedFiles[0]) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            setValue(
              'logo',
              {
                file,
                base64: reader.result
              },
              { shouldValidate: true, shouldDirty: true }
            );
            setPreviewImage(reader.result as string);
          }
        };
        reader.readAsDataURL(file);
      }
    },
    [setValue]
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/svg+xml': ['.svg'] },
    multiple: false
  });

  const { primaryColour, hasActivityHistory } = watch();
  const [saveState, setSaveState] = useState<SettingsSaveState>('idle');
  const [saveFailed, setSaveFailed] = useState(false);
  const { selectedOrganisationId } = useAuthStore();

  const goToTab = (tab: SettingsTabEnum) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    params.set('tab', tab);
    setSearchParams(params);
  };

  useEffect(() => {
    if (isSettingsTab(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
      return;
    }
    if (!isSettingsTab(tabParam) && tabParam !== null) {
      const params = new URLSearchParams(searchParams);
      params.set('tab', SettingsTabEnum.ORGANISATION);
      setSearchParams(params, { replace: true });
    }
  }, [tabParam, activeTab, searchParams, setSearchParams]);

  const onSubmit = (data: any) => {
    if (!selectedOrganisationId) {
      return;
    }
    const param: any = {
      organisationId: selectedOrganisationId,
      notificationEmail: data.notificationEmail,
      brokerEmails: data.brokerEmails ? [data.brokerEmails] : [],
      supportEmails: data.supportEnquiryEmail ? [data.supportEnquiryEmail] : [],
      primaryColour: data.primaryColour.split('#')[1],
      hasActivityHistory: Boolean(data.hasActivityHistory)
    };

    // Only send logoBase64 if the logo has been changed (has base64 data)
    if (data.logo.base64 && data.logo.base64.includes('base64,')) {
      param.logoBase64 = data.logo.base64.split('base64,')[1];
    }

    setSaveFailed(false);
    setSaveState('saving');
    post(UPDATE_LENDER_ORGANISATION, param, false)
      .then(() => {
        reset(getValues());
        setSaveState('saved');
      })
      .catch(() => {
        setSaveFailed(true);
        setSaveState('idle');
      });
  };

  const handleSave = () => {
    if (saveState !== 'idle' || !isDirty) {
      return;
    }
    if (!isValid) {
      trigger();
    }
    handleSubmit(onSubmit, (formErrors) => {
      const firstField = Object.keys(formErrors)[0];
      if (firstField) {
        goToTab(getSettingsTabForField(firstField));
      }
    })();
  };

  useEffect(() => {
    if (saveState !== 'saved') {
      return;
    }
    const timer = setTimeout(() => {
      setSaveState('idle');
    }, 2000);
    return () => clearTimeout(timer);
  }, [saveState]);
  const [orgName, setOrgName] = useState('');
  const navigator = useNavigate();
  const [roles, setRoles] = useState<string[]>([]);
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      navigator('/login');
      return;
    }

    const manageRoles = localStorage.getItem(`lenders_${userId}`);
    if (manageRoles) {
      const lenders = JSON.parse(manageRoles);
      if (lenders && lenders.length > 0) {
        const selectedLender =
          lenders.find(
            (lender: { organisationId?: string }) =>
              lender.organisationId === selectedOrganisationId
          ) || lenders[0];
        setRoles(selectedLender.roles || []);
      }
    }

    if (!selectedOrganisationId) {
      return;
    }

    let cancelled = false;
    post(
      GET_LENDER_ORGANISATION,
      { organisationId: selectedOrganisationId },
      false
    )
      .then((response) => {
        if (cancelled || !response?.lender) {
          return;
        }
        const data = response.lender;
        setOrgName(data.name);
        const supportEnquiryEmail =
          data.inspectionConfig.supportEmails &&
          data.inspectionConfig.supportEmails.length > 0
            ? data.inspectionConfig.supportEmails[0]
            : '';
        const brokerEmails =
          data.inspectionConfig?.brokerEmails &&
          data.inspectionConfig.brokerEmails.length > 0
            ? data.inspectionConfig.brokerEmails[0]
            : '';
        reset({
          primaryColour: `#${data.primaryColour}`,
          notificationEmail: data.inspectionConfig.notificationEmail,
          supportEnquiryEmail,
          brokerEmails,
          hasActivityHistory: Boolean(data.inspectionConfig?.hasActivityHistory),
          logo: data.logoUrl
            ? {
                file: null,
                url: data.logoUrl
              }
            : {}
        });
        setPreviewImage(data.logoUrl || '');
        setSaveFailed(false);
        setSaveState('idle');
      })
      .catch((e) => {
        if (!cancelled) {
          console.error(e);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [navigator, reset, selectedOrganisationId]);
  if (roles.includes(RoleEnum.STANDARD)) {
    return (
      <div style={{ maxWidth: '520px' }} className="p-2">
        <h1 className="font-semibold text-xl text-gray-900 mb-6">
          You don&apos;t have permission to access this page.
        </h1>
      </div>
    );
  }
  return (
    <div className="w-full pb-28">
      <UnderlineTabBar
        tabs={SETTINGS_TABS}
        activeTab={activeTab}
        onChange={goToTab}
        ariaLabel="Settings sections"
        idPrefix="settings"
      />

      <div className="mx-auto w-full max-w-[760px]">
      <div
        id={`settings-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`settings-tab-${activeTab}`}
        className="pt-6">
        {activeTab === SettingsTabEnum.ORGANISATION && (
          <div>
            <h2 className="text-[20px] font-semibold text-grey-900">
              Organisation
            </h2>
            <p className="mb-6 mt-1 text-sm text-grey-500">
              The organisation name shown to sellers and on inspection reports.
            </p>
            <div className="max-w-[520px]">
              <label
                className="mb-2 block text-sm font-medium text-gray-700"
                htmlFor="initials">
                Organisation Name
              </label>
              <Input
                type="text"
                placeholder="Organization name"
                value={orgName}
                disabled
              />
              <p className="mt-2 text-sm text-gray-500">
                Managed by Authentik8. Contact support to change your
                organisation name.
              </p>
            </div>
            <div className="mt-10">
              <MfaSettingPanel showTitle={true} />
            </div>
          </div>
        )}

        {activeTab === SettingsTabEnum.NOTIFICATIONS && (
          <div>
            <h2 className="text-[20px] font-semibold text-grey-900">
              Notifications
            </h2>
            <p className="mb-6 mt-1 text-sm text-grey-500">
              Configure where Authentik8 sends operational notification emails.
            </p>
            <div className="mb-8 max-w-[520px]">
              <label
                className="mb-2 block text-sm font-medium text-gray-700"
                htmlFor="notificationEmail">
                Notification Email Address
              </label>
              <Input
                type="email"
                id="notificationEmail"
                name="notificationEmail"
                autoComplete="email"
                placeholder="Notification Email"
                {...register(`notificationEmail`)}
                icon={errors.notificationEmail && <ErrorIcon />}
              />
              {errors.notificationEmail && (
                <p>
                  <span className="text-xs text-error-light">
                    {errors.notificationEmail.message}
                  </span>
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Receives inspection submission notifications.
              </p>
            </div>
            <div className="mb-8 max-w-[520px]">
              <label
                className="mb-2 block text-sm font-medium text-gray-700"
                htmlFor="brokerEmails">
                Broker Access Request Notification Email Address (Optional)
              </label>
              <Input
                type="email"
                id="brokerEmails"
                name="brokerEmails"
                autoComplete="off"
                placeholder="Broker Access Notification Email"
                {...register(`brokerEmails`)}
                icon={errors.brokerEmails && <ErrorIcon />}
              />
              {errors.brokerEmails && (
                <p>
                  <span className="text-xs text-error-light">
                    {errors.brokerEmails.message}
                  </span>
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                If provided, broker access request notifications will be sent to
                this address when a broker requests access to this lender. If
                left blank, broker access request notifications will not be
                sent.
              </p>
            </div>
            <div className="mb-4 max-w-[520px]">
              <label
                className="mb-2 block text-sm font-medium text-gray-700"
                htmlFor="supportEnquiryEmail">
                (API User) Support Email Address (Optional)
              </label>
              <Input
                type="email"
                id="supportEnquiryEmail"
                name="supportEnquiryEmail"
                autoComplete="off"
                placeholder="Support Enquiry Email"
                {...register(`supportEnquiryEmail`)}
                icon={errors.supportEnquiryEmail && <ErrorIcon />}
              />
              {errors.supportEnquiryEmail && (
                <p>
                  <span className="text-xs text-error-light">
                    {errors.supportEnquiryEmail.message}
                  </span>
                </p>
              )}
              <p className="mt-2 text-sm text-gray-500">
                If provided, all support enquiry emails for inspections created
                via the API will be sent here. By default, support enquiries are
                sent to the inspection creator and the Authentik8 support team.
              </p>
            </div>
          </div>
        )}

        {activeTab === SettingsTabEnum.SELLER && (
          <div>
            <h2 className="text-[20px] font-semibold text-grey-900">
              Seller inspection interface
            </h2>
            <p className="mb-6 mt-1 text-sm text-grey-500">
              Customise the Seller&apos;s inspection experience with your logo
              and brand colours
            </p>
            <div className="mb-6 max-w-[520px]">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Icon and Button Colour (HEX)
              </label>
              <div className="mb-2 flex">
                <Input
                  type="text"
                  {...register(`primaryColour`)}
                  icon={errors.primaryColour && <ErrorIcon />}
                  placeholder="Icon and Button Colour"
                />
                <label className="relative ml-2 h-10 w-10 flex-shrink-0 cursor-pointer">
                  <span
                    aria-hidden="true"
                    style={{ backgroundColor: primaryColour }}
                    className="absolute inset-0 rounded-[10px] border border-grey-300"
                  />
                  <input
                    type="color"
                    aria-label="Choose icon and button colour"
                    value={toColorPickerValue(primaryColour)}
                    onChange={(event) =>
                      setValue('primaryColour', event.target.value, {
                        shouldDirty: true,
                        shouldValidate: true
                      })
                    }
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  />
                </label>
              </div>
              {errors.primaryColour && (
                <p>
                  <span className="text-xs text-error-light">
                    {errors.primaryColour.message}
                  </span>
                </p>
              )}{' '}
              <p className="text-sm text-gray-500">
                For best result, use a dark colour.
              </p>
            </div>
            <div className="mb-6 max-w-[520px]">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Logo
              </label>
              <div
                className="upload-text-highlight cursor-pointer rounded-[10px] border border-gray-300 p-6 text-center text-gray-500"
                {...getRootProps()}
                onClick={() => document.getElementById('logo')?.click()}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <UploadIcon />
                </div>
                <p className="text-[14px] font-bold text-gray-600">
                  Drag and drop SVG File or Browse File to upload
                </p>
                <p className="text-sm">open and select your files manually.</p>
                <p className="md:hidden">Click to select files.</p>
                <input {...getInputProps()} id="logo" hidden />
              </div>
              {errors.logo && (
                <span className="text-xs text-error-light">
                  {errors.logo.message}
                </span>
              )}{' '}
            </div>
            {previewImage && (
              <div className="mt-4 flex max-w-[520px] flex-col">
                <p className="font-bold">Preview:</p>
                <img
                  src={previewImage}
                  alt="Uploaded SVG"
                  className="mt-2 h-auto max-w-full"
                />
              </div>
            )}
          </div>
        )}

        {activeTab === SettingsTabEnum.FEATURES && (
          <div>
            <h2 className="text-[20px] font-semibold text-grey-900">
              Features
            </h2>
            <p className="mb-6 mt-1 text-sm text-grey-500">
              Turn optional portal features on for everyone in this
              organisation.
            </p>
            <div className="flex items-start justify-between gap-6 rounded-lg border border-grey-200 bg-white px-6 py-5 hover:bg-grey-50">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-grey-900">
                    Activity history
                  </span>
                  <span
                    className={clsx(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      hasActivityHistory
                        ? 'bg-success-50 text-success-700'
                        : 'bg-grey-100 text-grey-600'
                    )}>
                    {hasActivityHistory ? 'On' : 'Off'}
                  </span>
                </div>
                <p className="mt-1.5 max-w-[460px] text-sm text-grey-500">
                  Shows an interactive map on the inspection detail page with a
                  chronological view of where and when inspection documents were
                  submitted.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-label="Activity history"
                aria-checked={Boolean(hasActivityHistory)}
                onClick={() =>
                  setValue('hasActivityHistory', !hasActivityHistory, {
                    shouldDirty: true
                  })
                }
                className={clsx(
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2',
                  hasActivityHistory ? 'bg-secondary' : 'bg-grey-200'
                )}>
                <span
                  aria-hidden="true"
                  className={clsx(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    hasActivityHistory ? 'translate-x-5' : 'translate-x-0'
                  )}
                />
              </button>
            </div>
          </div>
        )}
      </div>

      <SettingsTabNav activeTab={activeTab} onChange={goToTab} />
      </div>
      <SettingsSaveBar
        dirty={isDirty}
        saveState={saveState}
        saveFailed={saveFailed}
        onSave={handleSave}
      />
    </div>
  );
};

export default AccountSetting;
